// @flow

import type {SearchRequest} from '../models/SearchRequest';
import RangeParam from '../models/RangeParam';
import {
    ADULTS_COUNT,
    CHILDREN_COUNT,
    PET_INCLUDED,
    ARRIVAL,
    DEPARTURE,
    MIN_PRICE,
    MAX_PRICE,
    REGION,
    SLEEPS,
    FROM_DATE,
    TO_DATE,
    NE_LAT,
    NE_LONG,
    SW_LAT,
    SW_LONG,
    ZOOM,
    FILTER,
    ORDER_BY,
    ASCENDING,
    KEYWORDS,
    QUERY,
    VIEW,
    BEDROOMS,
    BEDROOMS_REFINEMENT,
    BATHROOMS,
    BATHROOMS_REFINEMENT,
    PAGE,
    SEARCH_TERM_UUID
} from '../models/SearchUrlParameters';
import {
    REGION_COLON_PARAMETER_VARIANTS,
    SEARCH_URL_PARAMETERS
} from '../models/SearchUrl';
import {decodeFilterValue} from './filterRefinementUtils';

import {
    intParser,
    stringParser,
    booleanParser,
    floatParser,
    formatDateToISO,
    multiPrimitiveParser,
    stripToRoot,
    isValidRoot
} from '../utils';

const VRBO_REFINEMENT_FIELD_TITLES = [
    'Even More Filters',
    'Features',
    'Amenities',
    'AddOn',
    'Suitabilitites',
    'Booking Options',
    'Online Booking',
    'Location Type',
    'LocationType',
    'Managed By',
    'New Properties',
    'Other',
    'Pay Per Booking',
    'Payment',
    'Property Type',
    'PropertyType',
    'Properties good for',
    'Suitability'
];

export type Logger = (message: string, key: string | void) => void;

export type Options = {
    onWarning?: Logger
};

export default class SearchUrlParser {
    DATE_FORMATS: RegExp[] = [
        new RegExp(/([0-9]{4})-([0-9]{2})-([0-9]{2})/),
        new RegExp(/([0-9]{2})\/([0-9]{2})\/([0-9]{4})/)
    ];

    +parse: (url: string, options: Options) => SearchRequest;

    getUrlParts(url: string): string[] {
        const root = stripToRoot(url);

        if (!isValidRoot(root)) {
            throw new Error(`Invalid root for url = {${url}}`);
        }

        const parts: string[] = this.stripSegment(url).split(root);

        if (parts.length !== 2) {
            throw new Error(`Malformed URL = {${url}}`);
        }

        return parts;
    }

    parseQueryParams(
        searchRequestModel: SearchRequest,
        options: Options,
        queryParameters: Object
    ) {
        if (Object.keys(queryParameters).length) {
            this.setKeywords(searchRequestModel, queryParameters);
            this.setPrice(searchRequestModel, queryParameters);
            this.setBedrooms(searchRequestModel, queryParameters);
            this.setBathrooms(searchRequestModel, queryParameters);
            this.setSleeps(searchRequestModel, queryParameters);
            this.setRegion(searchRequestModel, queryParameters);
            this.setDates(searchRequestModel, options, queryParameters);
            this.setGuests(searchRequestModel, queryParameters);
            this.setPage(searchRequestModel, queryParameters);
            this.setView(searchRequestModel, queryParameters);
            this.setOrderBy(searchRequestModel, queryParameters);
            this.setFilters(searchRequestModel, queryParameters);
            this.setBoundingBoxParams(searchRequestModel, queryParameters);
            this.setSearchTermUuid(searchRequestModel, queryParameters);
        }

        // for params not handled above, parse them, guessing the primitive type
        const handledParams = SEARCH_URL_PARAMETERS.concat(
            VRBO_REFINEMENT_FIELD_TITLES
        ).map((k) => k.toLowerCase());

        Object.keys(queryParameters).forEach((key) => {
            const value = queryParameters[key];
            if (!handledParams.includes(key.toLowerCase())) {
                searchRequestModel[key] = multiPrimitiveParser(value);
            }
        });
    }

    isValidPathname(pathname: string): boolean {
        return !!pathname && pathname.includes('/');
    }

    parsePathnameSegments(pathname: string): string[] {
        return pathname
            .split('/')
            .filter((segment) => segment !== '' && !segment.includes(':'))
            .map(encodeURIComponent);
    }

    setLegacyRegionPath(searchRequestModel: SearchRequest, segments: string[]) {
        if (segments && segments.length) {
            searchRequestModel.legacyRegionPath = segments.join(',');
        }
    }

    isRegionPathParam(paramName: string) {
        return REGION_COLON_PARAMETER_VARIANTS.some((regionParamName) => {
            // $FlowFixMe options string[] incorrect type checking
            const matcher = new RegExp(regionParamName, ['i']);
            return paramName.match(matcher) !== null;
        });
    }

    setGuests(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[ADULTS_COUNT]) {
            searchRequest.adultsCount = intParser(
                queryParameters[ADULTS_COUNT]
            );
        }

        if (queryParameters[CHILDREN_COUNT]) {
            searchRequest.childrenCount = intParser(
                queryParameters[CHILDREN_COUNT]
            );
        }

        if (queryParameters[PET_INCLUDED]) {
            searchRequest.petIncluded = booleanParser(
                queryParameters[PET_INCLUDED]
            );
        }
    }

    setDates(
        searchRequest: SearchRequest,
        options: Options,
        queryParameters: Object
    ) {
        if (
            (queryParameters[FROM_DATE] && queryParameters[TO_DATE]) ||
            (queryParameters[ARRIVAL] && queryParameters[DEPARTURE])
        ) {
            let arrival = decodeURIComponent(
                stringParser(
                    queryParameters[FROM_DATE] || queryParameters[ARRIVAL]
                )
            );
            let departure = decodeURIComponent(
                stringParser(
                    queryParameters[TO_DATE] || queryParameters[DEPARTURE]
                )
            );

            this.DATE_FORMATS.filter(
                (pattern: RegExp): boolean => {
                    return pattern.test(arrival) && pattern.test(departure);
                }
            ).forEach(() => {
                const parsedArrival = this.safeDateParse(
                    searchRequest,
                    arrival,
                    'arrival',
                    options
                );
                const parsedDeparture = this.safeDateParse(
                    searchRequest,
                    departure,
                    'departure',
                    options
                );
                if (parsedArrival && parsedDeparture) {
                    searchRequest.arrival = parsedArrival;
                    searchRequest.departure = parsedDeparture;
                }
            });
        }
    }

    safeDateParse(
        searchRequest: SearchRequest,
        value: string,
        key: 'arrival' | 'departure',
        options: Options
    ) {
        const dateValue = formatDateToISO(value);
        if (dateValue) {
            return dateValue;
        }
        if (options.onWarning) {
            options.onWarning(`Invalid ${key} date value=${value}`, key);
        }
        return false;
    }

    setRegion(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[REGION]) {
            searchRequest.regionId = intParser(queryParameters[REGION]);
        }
    }

    setSleeps(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[SLEEPS]) {
            let values: RangeParam<number> = this.parseRangeParam(
                queryParameters[SLEEPS]
            );

            if (values.getFrom()) {
                searchRequest.minSleeps = values.getFrom();
            }

            if (values.getTo()) {
                searchRequest.maxSleeps = values.getTo();
            }
        }
    }

    setBathrooms(searchRequest: SearchRequest, queryParameters: Object) {
        if (
            queryParameters[BATHROOMS] ||
            queryParameters[BATHROOMS_REFINEMENT]
        ) {
            let values: RangeParam<number> = this.parseRangeParam(
                queryParameters[BATHROOMS] ||
                    queryParameters[BATHROOMS_REFINEMENT]
            );

            if (values.getFrom()) {
                searchRequest.minBathrooms = values.getFrom();
            }

            if (values.getTo()) {
                searchRequest.maxBathrooms = values.getTo();
            }
        }
    }

    setBedrooms(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[BEDROOMS] || queryParameters[BEDROOMS_REFINEMENT]) {
            let values: RangeParam<number> = this.parseRangeParam(
                queryParameters[BEDROOMS] ||
                    queryParameters[BEDROOMS_REFINEMENT]
            );

            if (typeof values.getFrom() === 'number') {
                searchRequest.minBedrooms = values.getFrom();
            }

            if (typeof values.getTo() === 'number') {
                searchRequest.maxBedrooms = values.getTo();
            }
        }
    }

    setFilters(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[FILTER]) {
            const stringValue = stringParser(queryParameters[FILTER]);
            const arrayParam = (stringValue && stringValue.split(',')) || [];

            // prepend 'filter:' if just the integer is passed in
            searchRequest.filter = [].concat(
                searchRequest.filter || [],
                arrayParam.map((filter) =>
                    filter.match(/^\d+$/) ? `${FILTER}:${filter}` : filter
                )
            );
        }

        VRBO_REFINEMENT_FIELD_TITLES.forEach((propertyKey) => {
            this.setQueryParamFilters(
                searchRequest,
                queryParameters,
                propertyKey
            );
        });
    }

    setQueryParamFilters(
        searchRequest: SearchRequest,
        queryParameters: Object,
        key: string
    ) {
        let filters = searchRequest.filter ? searchRequest.filter : [];

        // query param key check
        Object.keys(queryParameters).forEach((k) => {
            const value = queryParameters[k];
            if (k === key) {
                const stringValue = stringParser(value);
                let tempFilters = (stringValue && stringValue.split(',')) || [];

                searchRequest.filter = filters.concat(
                    tempFilters.map(
                        (filter) =>
                            `${decodeFilterValue(key)}:${decodeFilterValue(
                                filter
                            )}`
                    )
                );
            }
        });
    }

    setPrice(searchRequest: SearchRequest, queryParameters: Object) {
        const price: RangeParam<number> = this.parsePriceParams(
            queryParameters
        );

        if (typeof price.getFrom() !== 'undefined') {
            searchRequest.minPrice = price.getFrom();
        }

        if (typeof price.getTo() !== 'undefined') {
            searchRequest.maxPrice = price.getTo();
        }
    }

    setKeywords(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[QUERY] || queryParameters[KEYWORDS]) {
            const keywords: ?string =
                queryParameters[QUERY] || queryParameters[KEYWORDS];

            const result = stringParser(keywords);

            if (result !== '-1') {
                searchRequest.q = result;
            }
        }
    }

    setPage(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[PAGE]) {
            searchRequest.page = intParser(queryParameters[PAGE]);
        }
    }

    setView(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[VIEW]) {
            searchRequest.view = stringParser(queryParameters[VIEW]);
        }
    }

    setOrderBy(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[ORDER_BY]) {
            searchRequest.orderBy = stringParser(queryParameters[ORDER_BY]);
            searchRequest.ascending = booleanParser(queryParameters[ASCENDING]);
        }
    }

    setBoundingBoxParams(
        searchRequest: SearchRequest,
        queryParameters: Object
    ) {
        if (
            queryParameters[NE_LAT] &&
            queryParameters[NE_LONG] &&
            queryParameters[SW_LAT] &&
            queryParameters[SW_LONG]
        ) {
            searchRequest.neLat = floatParser(queryParameters[NE_LAT]);
            searchRequest.neLong = floatParser(queryParameters[NE_LONG]);
            searchRequest.swLat = floatParser(queryParameters[SW_LAT]);
            searchRequest.swLong = floatParser(queryParameters[SW_LONG]);
        }
        if (queryParameters[ZOOM]) {
            searchRequest.zoom = intParser(queryParameters[ZOOM]);
        }
    }

    setSearchTermUuid(searchRequest: SearchRequest, queryParameters: Object) {
        if (queryParameters[SEARCH_TERM_UUID]) {
            searchRequest.searchTermUuid = stringParser(
                queryParameters[SEARCH_TERM_UUID]
            );
        }
    }

    parsePriceParams(queryParams: Object): RangeParam<number> {
        let result: RangeParam<number> = new RangeParam();

        if (queryParams[MIN_PRICE]) {
            let minPrice: number = intParser(queryParams[MIN_PRICE]);

            if (minPrice >= 0) {
                result.setFrom(minPrice);
            }
        }

        if (queryParams[MAX_PRICE]) {
            let maxPrice: number = intParser(queryParams[MAX_PRICE]);

            if (maxPrice >= 0) {
                result.setTo(maxPrice);
            }
        }

        return result;
    }

    parseRangeParam(value: ?string): RangeParam<number> {
        let result: RangeParam<number> = new RangeParam();

        if (value && !this.isEmpty(value)) {
            let parts: string[] = value.split('-');

            if (parts.length > 0) {
                let from: number = intParser(parts[0]);

                if (from >= 0) {
                    result.setFrom(from);
                }
            }

            if (parts.length === 1) {
                result.setTo(result.getFrom());
            } else if (parts.length > 1) {
                let to: number = intParser(parts[1]);

                if (to >= 0) {
                    result.setTo(to);
                }
            }
        }

        return result;
    }

    stripSegment(uri: string): string {
        if (this.isEmpty(uri)) {
            return uri;
        }

        return uri.split('#')[0];
    }

    isEmpty(uri: ?string): boolean {
        return !uri || uri.trim() === '';
    }
}
